import { useEffect } from 'react';

interface UseClickOutsideModalParams {
	isModalOpen: boolean;
	activeTextAreaElement: HTMLTextAreaElement | null;
	onClose: () => void;
	modalClassName?: string;
	textAreaContainerClassName?: string;
}

/**
 * Custom hook to handle clicking outside of a modal to close it
 * Provides specific logic for symbol selector modal with textarea interactions
 */
export const useClickOutsideModal = ({
	isModalOpen,
	activeTextAreaElement,
	onClose,
	modalClassName = '.symbols-modal',
	textAreaContainerClassName = '.textAreaContainer'
}: UseClickOutsideModalParams): void => {
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (!isModalOpen) {
				return;
			}

			const target = event.target as HTMLElement;

			// Don't close if clicking inside the modal
			if (target.closest(modalClassName)) {
				return;
			}

			// Don't close if clicking on the active text area or its controls
			if (activeTextAreaElement) {
				const textAreaContainer = activeTextAreaElement.closest(textAreaContainerClassName);
				if (textAreaContainer && textAreaContainer.contains(target)) {
					return;
				}
				if (activeTextAreaElement.contains(target)) {
					return;
				}
			}

			// Close the modal for any other clicks
			onClose();
		};

		if (isModalOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}
	}, [isModalOpen, activeTextAreaElement, onClose, modalClassName, textAreaContainerClassName]);
};
